class ProductCard extends HTMLElement {
    static _selectorInitialized = false;
    static _pointerInitialized = false;
    static _lastPointer = null;
    static _cardObserver = null;
    // WebKit-only check used to gate the trackpad gallery-flip wheel handler
    // (see _initGalleryWheel) away from Safari, where it's proven unfixable.
    static _isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
    static _videoObserver = null;

    constructor() {
        super();
        this.swiper = null;
        this._isAtcProcessing = false;
    }

    connectedCallback() {
        this.swiperEl = this.querySelector('swiper-container');
        this.falseImage = this.querySelector('.false-image-hover');
        this.cardWrapper = this.querySelector('.product-card-wrapper');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const parsedDelay = parseInt(this.dataset.sidecartDelay, 10);
        this._sidecartDelay = Number.isFinite(parsedDelay) ? parsedDelay : 4000;
        this._sidecartTimer = null;
        const isSoldOut = this.querySelector('.card--sold-out') !== null;
        const isComingSoon = this.querySelector('.card--coming-soon') !== null;
        const isInCarouselContext = this.closest('.related-products__swiper, .swiper, .slider, .slider__slide, .swiper-slide') !== null;
        this._isInCarouselContext = isInCarouselContext;
        this.cardWrapper?.classList.toggle('is-carousel-context', isInCarouselContext);
        this._pageLoaded = document.readyState === 'complete';
        if (!this._pageLoaded) {
            this._onPageLoad = () => {
                this._pageLoaded = true;
                this._revealReadyImages();
            };
            window.addEventListener('load', this._onPageLoad, { once: true });
        }
        // Constructing a real Swiper per card (loop:true slide cloning, nested/
        // mousewheel handlers, forced layout) turned out to be unsafe at scale —
        // confirmed by bisection that with dozens of cards' galleries initialized
        // eagerly from scroll proximity, mobile Safari (and even the Simulator,
        // which isn't memory constrained the same way) crashes outright. Instead
        // of ever eagerly initializing based on scroll position, only construct a
        // card's gallery Swiper the moment someone actually interacts with it
        // (desktop hover / mobile touch below), which keeps the number of live
        // instances near-zero during ordinary scrolling.
        this._needsSwiper = !!(this.swiperEl && !isComingSoon && !isSoldOut);
        // On touch devices the gallery has to exist *before* the swipe begins. A
        // Swiper built from the gesture that triggers it never sees the
        // touchstart that started the drag, so the first swipe does nothing —
        // and since <swiper-container init="false"> renders no slides until
        // initialize() runs, an uninitialized gallery also shows nothing at all.
        // Build it from viewport proximity here. Desktop still waits for hover,
        // where live instances stay bounded by the cards the pointer visits.
        if (this._needsSwiper && window.innerWidth < 1024) this._scheduleSwiperInit();
        this._observeLazyVideos();
        if (this.falseImage && !isSoldOut) this.initFalseImageHover();
        if (!isSoldOut) this.initSwatchHover();
        this.initImageReveal();
        this.initVideoReveal();
        this.addEventListener('click', this.onSizeClick.bind(this));
        this.addEventListener('click', this.onMobileQuickAddClick.bind(this));
        if (!ProductCard._pointerInitialized) {
            ProductCard._pointerInitialized = true;
            const updatePointer = (x, y) => {
                ProductCard._lastPointer = { x, y };
            };
            const updateFromEvent = (e) => {
                if (!e) return;
                updatePointer(e.clientX, e.clientY);
            };
            window.addEventListener('mousemove', updateFromEvent, { passive: true });
            window.addEventListener('pointerdown', updateFromEvent, { passive: true });
            window.addEventListener('touchstart', (e) => {
                const touch = e.touches && e.touches[0];
                if (!touch) return;
                updatePointer(touch.clientX, touch.clientY);
            }, { passive: true });
        }
        this._isHovering = false;
        this._onHoverEnter = () => {
            this._isHovering = true;
        };
        this._onHoverLeave = () => {
            this._isHovering = false;
            if (!this._sidecartTimer) {
                this.cardWrapper?.classList.remove('is-atc-open');
            }
            if (this.swiper) {
                this._resetting = true; 
                this.swiperEl?.classList.remove('is-swiped');
            }
        };
        this.cardWrapper?.addEventListener('mouseenter', this._onHoverEnter);
        this.cardWrapper?.addEventListener('mouseleave', this._onHoverLeave);

        this.cardInner = this.querySelector('.card__inner');
        const isLook = this.dataset.isLook === 'true';

        if (this.cardInner && !isSoldOut && !isLook) {
            this._onGalleryEnter = () => {
                if (window.innerWidth >= 1024 && !isComingSoon) {
                    this._ensureSwiperInitialized();
                    if (this.swiper) this.swiper.slideToLoop(1, 0);
                    if (this.falseImage) this.falseImage.classList.add('is-hidden');
                }
            };
            this._onGalleryLeave = () => {
                if (window.innerWidth >= 1024 && !isComingSoon) {
                    if (this.falseImage) this.falseImage.classList.remove('is-hidden');
                    if (this.swiper) {
                        this._resetting = true;
                        setTimeout(() => { this._resetting = false; }, 150);
                    }
                }
            };
            this.cardInner.addEventListener('mouseenter', this._onGalleryEnter);
            this.cardInner.addEventListener('mouseleave', this._onGalleryLeave);
        }

        if (!ProductCard._selectorInitialized) {
            ProductCard._initSharedSelector();
            ProductCard._selectorInitialized = true;
        }
    }

    disconnectedCallback() {
        if (this._outsideTouchHandler) {
            document.removeEventListener('touchstart', this._outsideTouchHandler);
        }
        if (this._onPageLoad) {
            window.removeEventListener('load', this._onPageLoad);
            this._onPageLoad = null;
        }
        if (this._onHoverEnter) {
            this.cardWrapper?.removeEventListener('mouseenter', this._onHoverEnter);
        }
        if (this._onHoverLeave) {
            this.cardWrapper?.removeEventListener('mouseleave', this._onHoverLeave);
        }
        if (this._onGalleryEnter) {
            this.cardInner?.removeEventListener('mouseenter', this._onGalleryEnter);
        }
        if (this._onGalleryLeave) {
            this.cardInner?.removeEventListener('mouseleave', this._onGalleryLeave);
        }
        if (this._onGalleryWheel) {
            this.swiperEl?.removeEventListener('wheel', this._onGalleryWheel);
        }
        if (this._onGalleryClickCapture) {
            this.swiperEl?.removeEventListener('click', this._onGalleryClickCapture, true);
        }
        clearTimeout(this._wheelResetTimer);

        clearTimeout(this._observerSetupTimer);
        this._disconnectCartObserver?.();
        this._lazyVideos?.forEach((video) => ProductCard._videoObserver?.unobserve(video));
        if (this._observedForSwiper) {
            ProductCard._cardObserver?.unobserve(this);
            this._observedForSwiper = false;
        }
        // Drop the live Swiper (and its loop clones) rather than leaving it for GC
        // to find via the detached subtree.
        if (this.swiper) {
            try { this.swiper.destroy(true, false); } catch (e) { /* noop */ }
            this.swiper = null;
        }
        this._pristineSwiperEl = null;
    }

    _ensureSwiperInitialized() {
        if (this.swiper || !this._needsSwiper) return;
        // swiper-element-bundle is no longer a `defer` tag in the head (see
        // theme.liquid) - it is fetched the first time a gallery is actually
        // wanted. Every caller of this method already null-checks `this.swiper`
        // afterwards, so an interaction that happens to race the download just
        // does not slide; the build completes and the next one does.
        if (!(window.customElements && customElements.get('swiper-container'))) {
            if (typeof window.loadSwiperElement !== 'function') return;
            window.loadSwiperElement().then(() => {
                if (!this.isConnected || this.swiper || !this._needsSwiper) return;
                this.intializeSwiper();
            }).catch(() => { /* CDN unreachable - card keeps its static image */ });
            return;
        }
        this.intializeSwiper();
    }

    // Build this card's gallery when it nears the viewport and tear it down again
    // once it is well past — touch devices only (see connectedCallback for why the
    // gesture-driven path cannot work there).
    //
    // The teardown half is the point. A Swiper per card is affordable; a Swiper per
    // card *retained for every card the session ever scrolled past* is not, and
    // that unbounded total is what has been getting the tab killed on long "Load
    // More" grids. Keeping the observer subscribed for the card's whole life (rather
    // than unobserving after the first build) bounds live instances to roughly what
    // is near the screen, no matter how far the user scrolls.
    _scheduleSwiperInit() {
        if (!('IntersectionObserver' in window)) {
            this._ensureSwiperInitialized();
            return;
        }
        // Pristine copy taken before the element is ever initialized — see
        // _teardownSwiper for why rebuilding needs one.
        if (!this._pristineSwiperEl) this._pristineSwiperEl = this.swiperEl.cloneNode(true);
        if (!ProductCard._cardObserver) {
            ProductCard._cardObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target._ensureSwiperInitialized();
                    } else {
                        entry.target._teardownSwiper();
                    }
                });
            }, { rootMargin: '600px' });
        }
        this._observedForSwiper = true;
        ProductCard._cardObserver.observe(this);
    }

    // Release this card's gallery. swiper-element has no supported way to
    // re-initialize after destroy() — initialize() early-returns on its own
    // `initialized` flag and would re-render its shadow root if forced — so
    // instead of fighting that, swap the spent element for a pristine clone
    // captured before it was ever initialized. A fresh custom element has no
    // shadow root and no initialized flag, so the normal build path works on it
    // unchanged when the card scrolls back.
    _teardownSwiper() {
        if (!this.swiper || !this._pristineSwiperEl) return;
        try {
            this.swiper.destroy(true, true);
        } catch (e) {
            /* already gone — fall through and still swap the element */
        }
        this.swiper = null;
        this.classList.remove('has-live-swiper', 'is-in-swiper');

        if (this._outsideTouchHandler) {
            document.removeEventListener('touchstart', this._outsideTouchHandler);
            this._outsideTouchHandler = null;
        }

        if (this._onGalleryWheel) {
            this.swiperEl.removeEventListener('wheel', this._onGalleryWheel);
            this._onGalleryWheel = null;
        }
        if (this._onGalleryClickCapture) {
            this.swiperEl.removeEventListener('click', this._onGalleryClickCapture, true);
            this._onGalleryClickCapture = null;
        }
        clearTimeout(this._wheelResetTimer);

        // Stop watching the videos that are about to be detached with the old
        // element, or the observer keeps them (and their decoders) referenced.
        this._lazyVideos?.forEach((video) => ProductCard._videoObserver?.unobserve(video));
        this._lazyVideos = null;

        const fresh = this._pristineSwiperEl.cloneNode(true);
        this.swiperEl.replaceWith(fresh);
        this.swiperEl = fresh;

        // Re-bind media on the replacement. Images are handled by the gallery's
        // own `init` hook when it is rebuilt; videos are not, and observing an
        // already-observed element is a no-op, so this is safe for the cover
        // video outside the gallery too.
        this._observeLazyVideos();
        this.initVideoReveal();
    }

    // A one-shot "load once and forget" observer isn't enough for video: a page with
    // many video-fronted cards (a search/PLP full of them) would still accumulate an
    // ever-growing number of concurrently *playing* decoders as the user scrolls,
    // since nothing ever pauses one once it's off-screen again. Keep observing for
    // the life of the card and pause/resume playback as it crosses the viewport, so
    // the number of active decoders stays bounded by what's near-screen right now
    // instead of growing with total scroll distance.
    _observeLazyVideos() {
        const lazyVideos = this.querySelectorAll('video.card-lazy-video');
        if (!lazyVideos.length) return;
        this._lazyVideos = lazyVideos;
        if (!('IntersectionObserver' in window)) {
            lazyVideos.forEach((video) => ProductCard._playLazyVideo(video));
            return;
        }
        if (!ProductCard._videoObserver) {
            ProductCard._videoObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    const video = entry.target;
                    if (entry.isIntersecting) {
                        ProductCard._playLazyVideo(video);
                    } else if (video.dataset.lazyLoaded === 'true') {
                        video.pause();
                    }
                });
            }, { rootMargin: '200px' });
        }
        lazyVideos.forEach((video) => ProductCard._videoObserver.observe(video));
    }

    static _playLazyVideo(video) {
        if (video.dataset.lazyLoaded !== 'true') {
            video.dataset.lazyLoaded = 'true';
            // The markup carries <source> children and preload="none", so nothing
            // has been fetched yet. load() starts it and lets the browser pick the
            // rendition it wants — which is why the sources stay in the markup
            // rather than being collapsed to one URL here.
            video.load();
        }
        video.play().catch(() => {});
    }

    intializeSwiper() {
        const isDesktop = window.innerWidth >= 1024;
        const isLook = this.dataset.isLook === 'true';

        // Opts this card into the GPU-layer promotions in component-card.css that
        // keep Swiper's transformed slides from flashing/escaping their clip. Those
        // promotions cost a permanent compositing buffer per card, so they are
        // scoped to cards that actually run a Swiper rather than applied to every
        // card in the grid.
        this.classList.add('has-live-swiper');

        // Gallery slide images carry data-src instead of src (see card-product.liquid)
        // so the browser never fetches/decodes them until the gallery is actually
        // initialized — otherwise every card would eagerly load both its always-
        // visible false-image AND every swiper slide behind it, doubling real photo
        // decode cost per card sitewide.
        this.querySelectorAll('.card-product-swiper img.card-lazy-slide-image[data-src]').forEach((img) => {
            img.src = img.dataset.src;
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        });

        Object.assign(this.swiperEl, {
            
            loop: true,
            slidesPerView: 1,
            spaceBetween: 0,
            // roundLengths floors each slide's width to an integer px. With a
            // single full-bleed slide per view (no shared edge to protect from
            // blur), that floor leaves the slide narrower than its fractional-
            // width container on many viewports, exposing a hairline sliver of
            // the next gallery image at the edge. Use the exact fractional
            // width instead so the slide always fills its container.
            initialSlide: isLook ? 0 : (isDesktop ? 1 : 0),
            simulateTouch: true,
            // This card swiper (image gallery) lives inside a slide of an outer
            // product carousel (home, related products, recently viewed) on
            // several pages. Letting it handle touch there means a normal swipe
            // just nudges the product photos instead of moving the outer
            // carousel. `isInCarouselContext` is computed synchronously in
            // connectedCallback (no dependency on whether/when the outer
            // carousel's own Swiper has finished initializing), so gate touch
            // dragging on it directly instead of waiting to detect the parent.
            allowTouchMove: !this._isInCarouselContext,
            nested: true,
            // Default threshold is 0px, so even a 1px jitter during a tap
            // (mouse click or finger tap) gets read as a drag. Swiper then
            // marks that pointer sequence as a swipe and swallows the
            // resulting click via preventClicks, so the product link needs a
            // second, motion-free click to register. Give taps some slack
            // before they count as a swipe.
            threshold: 10,
            // Even under the threshold slack above, Swiper's default preventClicks/
            // preventClicksPropagation can still swallow a tap that registered any
            // touchmove during a fast finger-down, leaving the product link dead on
            // first click until a second, motion-free click. Slide-drag is already
            // gated by threshold, so let clicks through untouched.
            preventClicks: false,
            preventClicksPropagation: false,
            // A touchpad/mouse-wheel gesture over a card fires wheel events, and
            // letting this nested gallery answer them flips the card's image
            // instead of scrolling the page (or the outer carousel, when nested
            // in one). Swiper's own forceToAxis re-checks the axis on every
            // tick, but Safari on macOS reports noisy, non-zero deltaX during
            // the inertial tail of a vertical scroll, so it kept mis-firing as
            // horizontal mid-scroll and freezing the page — every axis-lock
            // heuristic we tried in its place reproduced the same freeze, so
            // gallery-flip-by-wheel is handled ourselves and skipped on Safari
            // entirely (see _initGalleryWheel) instead of chasing the noise.
            mousewheel: false,
            navigation: {
                prevEl: this.querySelector('.card-product-swiper__prev'),
                nextEl: this.querySelector('.card-product-swiper__next'),
            },
            on: {
                init: () => this.initImageReveal(),
                transitionEnd: (swiper) => {
                    if (this._resetting) { this._resetting = false; return; }
                    if (window.innerWidth < 1024 && swiper.realIndex !== 0) {
                        this.swiperEl.classList.add('is-swiped');
                    }
                },
            },
        });
        this.swiperEl.initialize();
        this.swiper = this.swiperEl.swiper;

        this._onGalleryClickCapture = (e) => {
            if (this.swiper && this.swiper.allowClick === false) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        this.swiperEl.addEventListener('click', this._onGalleryClickCapture, true);

        // <swiper-container> renders its real .swiper-horizontal root inside a
        // shadow DOM, so the page's touch-action override in component-card.css
        // (which only reaches the light-DOM host) never applies to it. Set it
        // directly on the swiper's own element instead, so a touch that starts
        // on this nested gallery can still bubble up to the outer carousel.
        // Mobile/tablet only: desktop has no touch-drag to bubble, and leaving
        // this element's touch-action at auto on desktop Safari combined with
        // simulateTouch was freezing trackpad scroll over the nested gallery
        // (same WebKit wheel-noise family of bug as the outer carousel fix).
        if (this._isInCarouselContext && this.swiper?.el && !isDesktop) {
            this.swiper.el.style.touchAction = 'auto';
        }

        this._initGalleryWheel();

        if (this._isInCarouselContext) this.classList.add('is-in-swiper');
        if (this._outsideTouchHandler) {
            document.removeEventListener('touchstart', this._outsideTouchHandler);
        }
        this._outsideTouchHandler = (e) => {
            if (window.innerWidth >= 1024) return;
            if (this.swiperEl.contains(e.target)) return;
            if (!this.swiper) return;
            this._resetting = true;
            setTimeout(() => { this._resetting = false; }, 150); // fallback if transitionEnd doesn't fire
            this.swiper.slideToLoop(0, 0);
            this.swiperEl.classList.remove('is-swiped');
            if (this.falseImage) {
                this.falseImage.classList.remove('is-hidden');
                // Re-attach the touch listener so next swipe reveals swiper again
                const revealOnTouch = () => {
                    this.falseImage.classList.add('is-hidden');
                    this.swiperEl.removeEventListener('touchstart', revealOnTouch);
                };
                this.swiperEl.addEventListener('touchstart', revealOnTouch, { passive: true });
            }
        };
        document.addEventListener('touchstart', this._outsideTouchHandler);
    }

    // Trackpad/mouse-wheel gallery-flip for grid (standalone, non-carousel)
    // cards. Skipped on Safari entirely: WebKit reports noisy, non-zero
    // deltaX during the inertial tail of an ordinary vertical scroll, and
    // every axis-lock heuristic tried here (magnitude floor, dominance ratio,
    // one-way lock) still eventually mis-read that noise as horizontal and
    // froze Safari's scroll via preventDefault(). Chrome/Firefox/Edge
    // trackpads don't reproduce that noise, so the feature is safe there.
    // Also skipped for cards nested in an outer carousel so wheel events
    // bubble to that carousel/page untouched, matching allowTouchMove above.
    _initGalleryWheel() {
        if (this._isInCarouselContext || ProductCard._isSafari) return;

        const AXIS_MIN_DELTA = 2;
        const AXIS_RATIO = 1.5;
        const FLIP_THRESHOLD = 40;
        let axis = null;
        let accumX = 0;

        this._onGalleryWheel = (e) => {
            if (this.cardWrapper?.classList.contains('plp-first-card--animate')) return;

            const absX = Math.abs(e.deltaX);
            const absY = Math.abs(e.deltaY);
            clearTimeout(this._wheelResetTimer);
            this._wheelResetTimer = setTimeout(() => { axis = null; accumX = 0; }, 200);

            if (axis === null) {
                if (absX < AXIS_MIN_DELTA && absY < AXIS_MIN_DELTA) return;
                axis = absX > absY * AXIS_RATIO ? 'x' : 'y';
            } else if (axis === 'x' && absY >= AXIS_MIN_DELTA && absY > absX * AXIS_RATIO) {
                axis = 'y';
            }

            if (axis !== 'x') return;
            e.preventDefault();
            accumX += e.deltaX;
            if (Math.abs(accumX) < FLIP_THRESHOLD) return;
            this.swiper?.[accumX > 0 ? 'slideNext' : 'slidePrev']();
            accumX = 0;
        };
        this.swiperEl.addEventListener('wheel', this._onGalleryWheel, { passive: false });
    }

    initSwatchHover() {
        // Guard against duplicate initialization on reconnect
        if (this._swatchHoverInitialized) return;
        this._swatchHoverInitialized = true;

        const falseImg = this.falseImage?.querySelector('img.swatch-preview-image, img');
        if (!falseImg) return;
        // When the card's first media is a video, the swatch preview is a dedicated
        // overlay <img> shown on top of it rather than an in-place src swap.
        const isVideoFalse = falseImg.classList.contains('swatch-preview-image');
        const originalSrc = isVideoFalse ? '' : falseImg.src;
        const originalSrcset = isVideoFalse ? '' : falseImg.srcset;
        let _currentUrl = null;
        let _restoreTimer = null;
        // Track which URLs are already cached so we can skip the fade-in delay
        const _cachedUrls = new Set(isVideoFalse ? [] : [originalSrc]);

        const setFalseImage = (url, srcset) => {
            if (_currentUrl === url) return;
            _currentUrl = url;

            if (_cachedUrls.has(url)) {
                // Already cached — swap instantly, no flicker
                falseImg.src = url;
                falseImg.srcset = srcset || '';
                falseImg.classList.add('is-loaded');
            } else {
                // Not yet cached — go through the normal reveal fade-in
                falseImg.classList.remove('is-loaded');
                delete falseImg.dataset.revealBound;
                delete falseImg.dataset.revealed;
                falseImg.src = url;
                falseImg.srcset = srcset || '';
                falseImg.addEventListener('load', () => _cachedUrls.add(url), { once: true });
                this._revealImage(falseImg);
            }
        };

        this.querySelectorAll('.color-swatch[data-image]').forEach(swatch => {
            swatch.addEventListener('mouseenter', () => {
                clearTimeout(_restoreTimer);
                const url = swatch.dataset.image;
                if (!url || swatch.classList.contains('is-disabled')) return;
                setFalseImage(url, this._buildSrcset(url));
                this.falseImage.classList.remove('is-hidden');
                if (isVideoFalse) this.falseImage.classList.add('is-swatch-active');
            });
            swatch.addEventListener('mouseleave', () => {
                _restoreTimer = setTimeout(() => {
                    if (isVideoFalse) {
                        this.falseImage.classList.remove('is-swatch-active');
                        _currentUrl = null;
                    } else {
                        setFalseImage(originalSrc, originalSrcset);
                    }
                }, 50);
            });
        });
    }

    // Card slots are at most 25vw (desktop 4-col) / 50vw (mobile 2-col), so even on
    // a 3x display nothing above ~720px is ever used — offering 1500/2048 sources
    // only invites the browser to decode a multi-megabyte bitmap into a thumbnail
    // slot. Keep this list in step with the srcset widths in card-product.liquid.
    _buildSrcset(url) {
        const widths = [165, 360, 533, 720];
        return widths
            .map(w => `${url.replace(/width=\d+/, `width=${w}`)} ${w}w`)
            .join(', ');
    }

    initFalseImageHover() {
        // Mobile: false-image-hover sits visually on top of the swiper gallery
        // (z-index) and is a sibling of it, not an ancestor — so the gesture that
        // reveals the gallery has to be listened for on false-image itself
        // (what actually receives it) rather than on swiperEl, which would never
        // see it bubble through a sibling.
        //
        // A bare touchstart isn't enough to mean "swipe the gallery" — on a
        // touchscreen, ordinary vertical page-scrolling *also* fires touchstart
        // on whatever card the user's thumb happens to land on. Gating swiper
        // construction on touchstart alone meant plain scrolling through a long
        // list quietly initialized a Swiper instance per card touched along the
        // way, reproducing the exact crash this was meant to avoid. Only count
        // it once the drag is clearly horizontal (a real swipe), not vertical
        // (a scroll).
        if (window.innerWidth < 1024) {
            let startX = 0;
            let startY = 0;
            let tracking = false;
            const SWIPE_THRESHOLD = 10;

            const onTouchStart = (e) => {
                const touch = e.touches[0];
                if (!touch) return;
                startX = touch.clientX;
                startY = touch.clientY;
                tracking = true;
            };
            const reveal = () => {
                tracking = false;
                this._ensureSwiperInitialized();
                this.falseImage.classList.add('is-hidden');
                this.falseImage.removeEventListener('touchstart', onTouchStart);
                this.falseImage.removeEventListener('touchmove', onTouchMove);
                this.falseImage.removeEventListener('touchend', onTouchEnd);
            };
            const onTouchMove = (e) => {
                if (!tracking) return;
                const touch = e.touches[0];
                if (!touch) return;
                const dx = touch.clientX - startX;
                const dy = touch.clientY - startY;
                if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
                    reveal();
                } else if (Math.abs(dy) > SWIPE_THRESHOLD) {
                    tracking = false; // vertical scroll — not a gallery swipe
                }
            };
            const onTouchEnd = () => {
                tracking = false;
            };

            this.falseImage.addEventListener('touchstart', onTouchStart, { passive: true });
            this.falseImage.addEventListener('touchmove', onTouchMove, { passive: true });
            this.falseImage.addEventListener('touchend', onTouchEnd, { passive: true });
        }
    }

    initImageReveal() {
        this.querySelectorAll('.card__media img, .card-product-swiper img').forEach((img) => {
            this._revealImage(img);
        });
    }

    initVideoReveal() {
        this.querySelectorAll('.card__media-video').forEach((video) => {
            this._revealVideo(video);
        });
    }

    // Reveal a single element once it can paint. Each media reveals independently
    // so a slow/lazy/never-loading sibling (e.g. unloaded swiper slides on a sold-out
    // card) can't keep the visible cover image stuck behind the placeholder.
    _revealEl(el) {
        if (!el || el.dataset.revealed === 'true' || el.dataset.errored === 'true') return;
        el.dataset.revealed = 'true';
        // Was setTimeout(..., 50). The delay bought nothing - each element
        // reveals independently, so there was no batching to wait for - and it
        // sat directly in front of the paint that LCP is measured from on every
        // card. rAF still defers the class to the next frame, so the 300ms
        // opacity transition in component-card.css is not skipped.
        requestAnimationFrame(() => el.classList.add('is-loaded'));
    }

    _revealVideo(video) {
        if (!video) return;
        if (video.dataset.revealBound === 'true') return;
        video.dataset.revealBound = 'true';

        // readyState >= 2 (HAVE_CURRENT_DATA) means first frame is paintable
        if (video.readyState >= 2) {
            this._revealEl(video);
        } else {
            video.addEventListener('loadeddata', () => this._revealEl(video), { once: true });
        }
        video.addEventListener('error', () => {
            video.dataset.errored = 'true';
            const media = video.closest('.media');
            if (media) media.classList.add('is-error');
        }, { once: true });
    }

    _revealImage(img) {
        if (!img) return;
        if (img.dataset.revealBound === 'true') return;
        img.dataset.revealBound = 'true';

        if (img.complete && img.naturalWidth > 0) {
            this._revealEl(img);
        } else {
            img.addEventListener('load', () => this._revealEl(img), { once: true });
        }
        img.addEventListener('error', () => {
            img.dataset.errored = 'true';
            const media = img.closest('.media');
            if (media) media.classList.add('is-error');
        }, { once: true });
    }

    _revealReadyImages() {
        // Page finished loading — reveal anything already complete that hasn't fired yet.
        this.querySelectorAll('.card__media img, .card-product-swiper img').forEach((img) => {
            if (img.complete && img.naturalWidth > 0) this._revealEl(img);
        });
        this.querySelectorAll('.card__media-video').forEach((video) => {
            if (video.readyState >= 2) this._revealEl(video);
        });
    }

    // Desktop size overlay — direct add-to-cart on click
    onSizeClick(event) {
        const button = event.target.closest('.size-option');
        if (!button || !this.contains(button)) return;
        if (button.disabled) return;
        if (button.closest('.mobile-size__selector')) return; // handled by static listener
        if (this._isAtcProcessing) return;
        const variantId = button.dataset.variantId;
        if (!variantId) return;
        event.preventDefault();
        event.stopPropagation();
        button.classList.add('is-selected');
        this.addToCart(button, variantId);
    }

    // Mobile "+" button — direct ATC or open shared selector
    onMobileQuickAddClick(event) {
        const button = event.target.closest('.card-mobile-quick-add');
        if (!button || !this.contains(button)) return;
        if (button.disabled) return;
        if (this._isAtcProcessing) return;
        event.preventDefault();
        event.stopPropagation();

        // Single variant / One Size → add directly
        let variantId = button.dataset.variantId;
        if (!variantId) {
            // Fallback: no template means single-variant — grab ID from size-option if present
            const hasTemplate = !!this.querySelector('.card-mobile-size-template');
            if (!hasTemplate) {
                variantId = this.querySelector('.size-option[data-variant-id]')?.dataset.variantId;
            }
        }
        if (variantId) {
            this.addToCart(button, variantId);
            return;
        }

        // Multi-size → clone pre-rendered Liquid template into shared selector
        const selector = document.querySelector('.mobile-size__selector');
        const template = this.querySelector('.card-mobile-size-template');
        if (!selector || !template) return;

        selector.classList.remove('is-open', 'is-closing', 'is-bis');
        selector.innerHTML = '';
        selector.appendChild(template.content.cloneNode(true));
        selector._currentCard = this;

        // is-closing makes the sheet display:block at translateY(100%).
        // offsetHeight forces a synchronous layout so the browser registers
        // that start position before we switch to is-open to run the transition.
        selector.classList.add('is-closing');
        // eslint-disable-next-line no-unused-expressions
        selector.querySelector('.mobile-size__sheet')?.offsetHeight;
        selector.classList.replace('is-closing', 'is-open');
        selector.setAttribute('open', '');
        document.body.classList.add('mobile-popup-open');
        ProductCard._initKiwi(selector);
    }

    // ── Shared selector — one listener for the whole page ──────────────────
    static _initSharedSelector() {
        // Remove any duplicate containers, keep only the first
        const all = document.querySelectorAll('.mobile-size__selector');
        all.forEach((el, i) => { if (i > 0) el.remove(); });

        const selector = all[0];
        if (!selector || selector.dataset.initialized) return;
        selector.dataset.initialized = 'true';

        selector.addEventListener('click', (e) => {
            if (e.target.closest('.mobile-size__overlay')) {
                ProductCard._closeSelector(selector);
                return;
            }
            const sizeBtn = e.target.closest('.size-option');
            if (sizeBtn) {
                if (selector._currentCard?._isAtcProcessing) return;
                ProductCard._selectSize(selector, sizeBtn);
                return;
            }
            const atcBtn = e.target.closest('.mobile-size__atc');
            if (atcBtn && !atcBtn.disabled && atcBtn.dataset.variantId) {
                if (selector._currentCard?._isAtcProcessing) return;
                selector._currentCard?.addToCart(atcBtn, atcBtn.dataset.variantId);
                ProductCard._closeSelector(selector);
            }
        });

        selector.addEventListener('input', (e) => {
            const emailInput = e.target.closest('.mobile-size__email');
            if (!emailInput) return;
            const form = emailInput.closest('.mobile-size__bis');
            if (!form) return;
            const notifyBtn = form.querySelector('.mobile-size__notify');
            if (notifyBtn) notifyBtn.disabled = emailInput.value.trim().length === 0;
            // Clear error while typing
            const errEl = form.querySelector('[data-bis-error]');
            if (errEl) errEl.hidden = true;
        });

        selector.addEventListener('submit', (e) => {
            const form = e.target.closest('.mobile-size__bis');
            const notifyBtn = form.querySelector('.mobile-size__notify');
            if (!form) return;
            e.preventDefault();
            const emailInput = form.querySelector('.mobile-size__email');
            const email = emailInput?.value?.trim();
            const errEl = form.querySelector('[data-bis-error]');
            const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
            if (!isValid) {
                if (errEl) errEl.hidden = false;
                notifyBtn.disabled = true;
                return;
            }
            if (errEl) errEl.hidden = true;
            selector._currentCard?.triggerKlaviyoBIS(form, form.dataset.variantId, email);
        });
    }

    static _selectSize(selector, button) {
        selector.querySelectorAll('.size-option').forEach(b => b.classList.remove('is-selected'));
        button.classList.add('is-selected');

        const atcBtn  = selector.querySelector('.mobile-size__atc');
        const bisForm = selector.querySelector('.mobile-size__bis');
        const isSoldOut = button.classList.contains('is-sold-out');

        selector.classList.toggle('is-bis', isSoldOut);

        if (bisForm) {
            // Always reset BIS form state on every size change
            const emailInput = bisForm.querySelector('.mobile-size__email');

            if (emailInput && document.activeElement === emailInput) emailInput.blur();
            
            if (emailInput) { emailInput.value = ''; emailInput.removeAttribute('aria-invalid'); }
            const notifyBtn = bisForm.querySelector('.mobile-size__notify');
            if (notifyBtn) notifyBtn.disabled = true;
            const errEl = bisForm.querySelector('[data-bis-error]');
            if (errEl) errEl.hidden = true;
            const successEl = bisForm.parentElement?.querySelector('[data-bis-success]');
            if (successEl) successEl.hidden = true;

            if (isSoldOut) {
                bisForm.dataset.variantId = button.dataset.variantId || '';
                bisForm.hidden = false;
            } else {
                delete bisForm.dataset.variantId;
                bisForm.hidden = true;
            }
        }

        if (atcBtn) {
            atcBtn.disabled = isSoldOut;
            if (isSoldOut) {
                delete atcBtn.dataset.variantId;
            } else {
                atcBtn.dataset.variantId = button.dataset.variantId;
                atcBtn.textContent = atcBtn.dataset.atcText || atcBtn.textContent;
            }
        }
    }

    static _closeSelector(selector) {
        selector.classList.remove('is-open');
        selector.classList.add('is-closing');
        selector.removeAttribute('open');
        document.body.classList.remove('mobile-popup-open');

        const sheet = selector.querySelector('.mobile-size__sheet');
        const cleanup = () => {
            selector.classList.remove('is-closing', 'is-bis');
            selector._currentCard = null;
        };

        if (sheet) {
            sheet.addEventListener('transitionend', cleanup, { once: true });
        } else {
            cleanup();
        }
    }

    // ── Kiwi size chart ─────────────────────────────────────────────────────
    static _initKiwi(selector) {
        const waitForKiwi = (callback) => {
            const check = setInterval(() => {
                if (window.ks && typeof window.ks.loadSizing === 'function') {
                    clearInterval(check);
                    callback();
                }
            }, 200);
        };

        waitForKiwi(() => {
            setTimeout(() => ProductCard._loadKiwiChart(selector), 500);
        });
    }

    static _loadKiwiChart(modal) {
        const chart =
            modal.querySelector('#KiwiSizingChart') ||
            modal.querySelector('[data-kiwi-size-chart]');
        const productJSON = modal.querySelector('[data-product-json]');

        if (!chart) return;

        let product = {};
        if (productJSON) {
            try { product = JSON.parse(productJSON.textContent); } catch (e) { /* ignore */ }
        }

        if (chart.dataset.kiwiInitialized === 'true') return;
        chart.dataset.kiwiInitialized = 'true';
        chart.innerHTML = '';

        const displayMode = Number(chart.dataset.displayMode);
        const sizingOptions = { el: chart };
        if (!Number.isNaN(displayMode)) sizingOptions.displayMode = displayMode;
        if (chart.dataset.linkText) sizingOptions.buttonText = chart.dataset.linkText;

        window.ks.loadSizing({
            productData: {
                product: chart.dataset.product || product.id || product.handle,
                tags: chart.dataset.tags || (Array.isArray(product.tags) ? product.tags.join(',') : product.tags || ''),
                type: chart.dataset.type || product.product_type || '',
                vendor: chart.dataset.vendor || product.vendor || '',
                collections: chart.dataset.collections || '',
            },
            options: sizingOptions,
            loaded(_success) {
                ProductCard._clearKiwiDefaultSelection(chart);
                ProductCard._placeKiwiLink(modal, chart);
                setTimeout(() => ProductCard._placeKiwiLink(modal, chart), 300);
            },
        });
    }

    static _clearKiwiDefaultSelection(chart) {
        if (!chart) return;
        const classSelectors = ['.is-active', '.selected', '.active', '.kiwi-size-table__row--active', '.kiwi-size-chart__row--active', '.kiwi-size-chart__brand__size--active'];
        const boolSelectors = ['[aria-pressed="true"]', '[aria-checked="true"]', '[data-active="true"]'];
        classSelectors.forEach(sel => {
            chart.querySelectorAll(sel).forEach(el => {
                el.classList.remove('is-active', 'selected', 'active', 'kiwi-size-table__row--active', 'kiwi-size-chart__row--active', 'kiwi-size-chart__brand__size--active');
            });
        });
        boolSelectors.forEach(sel => {
            chart.querySelectorAll(sel).forEach(el => {
                if (el.hasAttribute('aria-pressed')) el.setAttribute('aria-pressed', 'false');
                if (el.hasAttribute('aria-checked')) el.setAttribute('aria-checked', 'false');
                if (el.hasAttribute('data-active')) el.setAttribute('data-active', 'false');
            });
        });
    }

    static _placeKiwiLink(modal, chart) {
        if (!modal || !chart) return;
        const targets = Array.from(modal.querySelectorAll('[data-kiwi-link-target]'));
        if (targets.length === 0) return;
        const target = targets[0];
        targets.slice(1).forEach(el => { el.innerHTML = ''; });

        const preferredSelectors = ['.ks-chart-modal-link', '.ks-chart-modal-button', '.ks-calculator-modal-link', '.ks-calculator-modal-button'];
        const linkSelectors = preferredSelectors.join(', ');
        let primaryLink = null;
        for (const sel of preferredSelectors) {
            primaryLink = chart.querySelector(sel) || modal.querySelector(sel);
            if (primaryLink) break;
        }
        if (!primaryLink) return;

        Array.from(modal.querySelectorAll(linkSelectors)).forEach(el => {
            if (el !== primaryLink) el.remove();
        });
        target.innerHTML = '';
        target.appendChild(primaryLink);
        chart.style.display = 'none';
    }

    triggerKlaviyoBIS(form, variantId, email) {
        if (!variantId || !email) return;

        const notifyBtn = form.querySelector('.mobile-size__notify');
        const successEl = form.parentElement?.querySelector('[data-bis-success]');

        if (notifyBtn) {
            notifyBtn.disabled = true;
        }

        const payload = {
            data: {
                type: 'back-in-stock-subscription',
                attributes: {
                    channels: ['EMAIL'],
                    profile: {
                        data: {
                            type: 'profile',
                            attributes: { email },
                        },
                    },
                },
                relationships: {
                    variant: {
                        data: {
                            type: 'catalog-variant',
                            id: `$shopify:::$default:::${variantId}`,
                        },
                    },
                },
            },
        };

        fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=WPfH8e', {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                revision: '2023-12-15',
            },
            body: JSON.stringify(payload),
        })
            .then((res) => {
                if (res.ok || res.status === 202) {
                    const sheet = form.closest('.mobile-size__sheet');
                    if (sheet) {
                        sheet.querySelectorAll('.mobile-size__sizes, .mobile-size__bis, .mobile-size__atc, .mobile-size__footer, [data-kiwi-size-chart]').forEach(el => { el.style.display = 'none'; });
                    }
                    if (successEl) successEl.hidden = false;
                } else {
                    throw new Error('BIS request failed');
                }
            })
            .catch(() => {
                if (notifyBtn) {
                    notifyBtn.disabled = false;
                    notifyBtn.textContent = notifyBtn.dataset.originalText || 'Notify Me';
                }
                let errEl = form.querySelector('.mobile-size__error');
                if (!errEl) {
                    errEl = document.createElement('p');
                    errEl.className = 'mobile-size__error';
                    form.appendChild(errEl);
                }
                errEl.textContent = 'Something went wrong. Please try again.';
            });
    }

    addToCart(button, variantId) {
        if (this._isAtcProcessing) return;
        if (button.dataset.loading === 'true') return;
        this._isAtcProcessing = true;
        button.dataset.loading = 'true';
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');
        this._activeButton = button;
        this._lastSwiperIndex = this.swiper ? this.swiper.realIndex : null;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        // Extract EU size from selected size option
        const sizeInput = this.querySelector('.size-option.is-selected');
        if (sizeInput && sizeInput.getAttribute('data-size') && sizeInput.getAttribute('data-size').includes(' / ')) {
            const parts = sizeInput.getAttribute('data-size').split(' / ');
            let euSize = null;
            for (const part of parts) {
                const trimmed = part.trim();
                const subParts = trimmed.split(' ');
                if (subParts.length > 1 && subParts[0].toLowerCase() === 'eu') {
                    euSize = subParts[1];
                    break;
                }
            }
            if (euSize) {
                formData.append('properties[Size]', `${euSize} | EU`);
            }
        }

        if (this.cart) {
            formData.append('sections', this.cart.getSectionsToRender().map((s) => s.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(button);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
            .then((r) => r.json())
            .then((response) => {
                if (response.status) {
                    button.classList.remove('is-selected');
                    publish(PUB_SUB_EVENTS.cartError, {
                        source: 'product-card',
                        productVariantId: variantId,
                        errors: response.errors || response.description,
                        message: response.message,
                    });
                    return;
                }
                if (!this.cart) { window.location = window.routes.cart_url; return; }

                const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-card',
                    productVariantId: variantId,
                    cartData: response,
                }).then(() => CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker));

                CartPerformance.measure('add:paint-updated-sections', () => {
                    this.cart.renderContents(response);
                });

                this._startSidecartTimer();
            })
            .catch((err) => {
                console.error(err);
                button.classList.remove('is-selected');
            })
            .finally(() => {
                button.dataset.loading = 'false';
                button.removeAttribute('aria-disabled');
                button.classList.remove('is-loading');
                if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                this._isAtcProcessing = false;
            });
    }
    _startSidecartTimer() {
        if (!this.cart) return;
        clearTimeout(this._sidecartTimer);
        clearTimeout(this._observerSetupTimer);
        this._observerSetupTimer = null;
        this._disconnectCartObserver?.();
        this.cardWrapper?.classList.add('is-atc-open');
        const cartOpenEl = this.cart.querySelector('#cart-notification') || this.cart;
        const isCartOpen = () => {
            if (!cartOpenEl) return false;
            return cartOpenEl.hasAttribute('open')
                || cartOpenEl.classList.contains('is-open')
                || cartOpenEl.classList.contains('active');
        };

        const cleanup = () => {
            // Disconnect first to prevent re-entry if observer fires after timer cleanup
            clearTimeout(this._observerSetupTimer);
            this._observerSetupTimer = null;
            this._disconnectCartObserver?.();
            this._disconnectCartObserver = null;
            clearTimeout(this._sidecartTimer);
            this._sidecartTimer = null;
            if (this._activeButton) {
                this._activeButton.classList.remove('is-selected');
                this._activeButton = null;
            }
            const isHovering = this._isHovering || this.cardWrapper?.matches(':hover') || this._isPointerOverCard();
            this._logPointerTarget('sidecart-close');
            if (isHovering) {
                this.cardWrapper?.classList.add('is-atc-open');
                if (this.swiper) this.swiper.slideToLoop(1, 0);
                if (this.falseImage) this.falseImage.classList.add('is-hidden');
            } else if (this.swiper && typeof this._lastSwiperIndex === 'number' && this.swiper.realIndex !== this._lastSwiperIndex) {
                this.cardWrapper?.classList.remove('is-atc-open');
                this.swiper.slideToLoop(this._lastSwiperIndex, 0);
            }
            if (!isHovering) {
                this.cardWrapper?.classList.remove('is-atc-open');
            }
            // Desktop: :hover CSS keeps size selector visible while still hovering — no extra work needed.
            // Mobile: sheet is already closed immediately on ATC tap.
        };

        // Delay observer start so the cart has time to fully open before we watch for it closing.
        // On first add after clearing cookies the cart goes through init class changes immediately,
        // which would fire the observer before 'is-open'/'open' is applied and prematurely cleanup.
        this._observerSetupTimer = setTimeout(() => {
            this._observerSetupTimer = null;
            const observer = new MutationObserver(() => {
                if (!isCartOpen()) cleanup();
            });
            if (cartOpenEl) {
                observer.observe(cartOpenEl, { attributes: true, attributeFilter: ['open', 'class'] });
            }
            this._disconnectCartObserver = () => observer.disconnect();
        }, 300);

        const drawerInner = this.cart.querySelector('.drawer__inner');
        const startCloseTimer = () => {
            this._sidecartTimer = setTimeout(() => {
                if (drawerInner?.matches(':hover')) {
                    startCloseTimer();
                    return;
                }
                this.cart.close?.();
                cleanup();
            }, this._sidecartDelay);
        };

        startCloseTimer();
    }

    _isPointerOverCard() {
        const point = ProductCard._lastPointer;
        if (!point || !this.cardWrapper) return false;
        const rect = this.cardWrapper.getBoundingClientRect();
        if (point.x < rect.left || point.x > rect.right || point.y < rect.top || point.y > rect.bottom) {
            return false;
        }
        const el = document.elementFromPoint(point.x, point.y);
        return !!el && this.cardWrapper.contains(el);
    }

    _logPointerTarget(context) {
        const point = ProductCard._lastPointer;
        if (!point) {
            return;
        }
        setTimeout(() => {
            const el = document.elementFromPoint(point.x, point.y);
            const elInfo = el
                ? `${el.tagName.toLowerCase()}${el.id ? `#${el.id}` : ''}${el.className ? `.${String(el.className).trim().replace(/\s+/g, '.')}` : ''}`
                : 'null';
            const inCard = el && this.cardWrapper ? this.cardWrapper.contains(el) : false;
            if (inCard) {
                this.cardWrapper?.classList.add('is-atc-open');
                if (this.swiper) this.swiper.slideToLoop(1, 0);
                if (this.falseImage) this.falseImage.classList.add('is-hidden');
            }
        }, 300);
    }
}
customElements.define('product-card', ProductCard);

// Preserve scroll position when Kiwi's modal locks/unlocks body overflow
(function () {
  if (window._kiwiScrollGuardInit) return;
  window._kiwiScrollGuardInit = true;

  let _savedScrollY = null;

  var kiwiBodyObserver = new MutationObserver(() => {
    const kiwiModalVisible =
      document.body.classList.contains('kiwi-sizing-modal-visible') ||
      document.documentElement.classList.contains('kiwi-sizing-modal-visible') ||
      !!document.querySelector('.kiwi-sizing-modal-visible, .vanilla-modal-visible');
    const locked =
      document.body.style.overflow === 'hidden' ||
      document.body.style.position === 'fixed' ||
      kiwiModalVisible;
    const isSidebarLock = !!document.querySelector('.pdp-sidebar.is-open, .pdp-sidebar.is-closing');

    if (kiwiModalVisible && !isSidebarLock && _savedScrollY === null) {
      const guard = window.__pdpKiwiScrollGuardState;
      const bodyTop = parseInt(document.body.style.top || '0', 10);
      if (guard && guard.hasSavedScroll) {
        _savedScrollY = guard.scrollY;
      } else if (bodyTop < 0) {
        _savedScrollY = -bodyTop;
      } else {
        _savedScrollY = window.scrollY;
      }
      if (!window.__pdpKiwiModalOpen && typeof window.__pdpKiwiPrepareOpen === 'function') {
        window.__pdpKiwiPrepareOpen(_savedScrollY);
      } else if (typeof window.__pdpKiwiSaveScrollPosition === 'function') {
        window.__pdpKiwiSaveScrollPosition(_savedScrollY);
      }
    } else if (!kiwiModalVisible && _savedScrollY !== null) {
      const y = _savedScrollY;
      _savedScrollY = null;
      if (typeof window.__pdpSettleAfterKiwiClose === 'function') {
        window.__pdpSettleAfterKiwiClose(y, null);
      } else {
        requestAnimationFrame(() => window.scrollTo(0, y));
      }
    }
  });
  kiwiBodyObserver.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
  if (document.documentElement) {
    kiwiBodyObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }
}());
